import { useEffect, useState } from 'react';
import { FormBuilderService } from '../api/FormBuilderService';
import {
  IJobQuestionnaireWithStats,
  IQuestionItem,
  IQuestionnaire,
  IQuestionnaireJobLink,
} from '../types/questionnaire';

// Fetch every questionnaire that has ever been attached to a job (active
// and soft-deleted), load each questionnaire definition once, and expose
// them as a single ordered list. Per-questionnaire response counts are
// computed by the caller from the already-loaded interviews since that
// data is owned by useInterviewManagement.
export const useJobQuestionnaireHistory = (jobId: number | null) => {
  const [links, setLinks] = useState<IQuestionnaireJobLink[]>([]);
  const [questionnairesById, setQuestionnairesById] = useState<Map<string, IQuestionnaire>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (jobId == null) {
      setLinks([]);
      setQuestionnairesById(new Map());
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const run = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const linkList = await FormBuilderService.getAllQuestionnaireLinksByJobId(jobId);
        if (cancelled) return;
        setLinks(linkList);

        const uniqueIds = Array.from(new Set(linkList.map(l => l.questionnaireId)));
        const fetched = await Promise.all(
          uniqueIds.map(async id => {
            try {
              const q = await FormBuilderService.getQuestionnaire(id);
              return [id, q] as const;
            } catch (err) {
              console.error(`Failed to load questionnaire ${id}`, err);
              return [id, null] as const;
            }
          })
        );
        if (cancelled) return;

        const map = new Map<string, IQuestionnaire>();
        fetched.forEach(([id, q]) => {
          if (q) map.set(id, q);
        });
        setQuestionnairesById(map);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [jobId]);

  return { links, questionnairesById, isLoading, error };
};

// Helper: build the UI aggregate list from links + loaded questionnaires +
// the interviews we already have in memory. Returns items in the order the
// API returned the links (CreatedAt DESC), grouped later in the UI by active.
export const buildQuestionnaireStats = (
  links: IQuestionnaireJobLink[],
  questionnairesById: Map<string, IQuestionnaire>,
  countByQuestionnaireId: Map<string, number>
): IJobQuestionnaireWithStats[] => {
  return links
    .map(link => {
      const q = questionnairesById.get(link.questionnaireId);
      if (!q) return null;
      const questions: IQuestionItem[] = (q.questions || []).map((question, idx) => ({
        id: idx,
        text: question.text || question.questionText || '',
      }));
      return {
        questionnaireId: link.questionnaireId,
        questionnaireName: q.questionnaireName,
        contentType: q.contentType,
        questions,
        active: link.active,
        responseCount: countByQuestionnaireId.get(link.questionnaireId) ?? 0,
        linkCreatedAt: link.createdAt,
      };
    })
    .filter((x): x is IJobQuestionnaireWithStats => x !== null);
};
